<script setup>
import { ref, computed, reactive, onMounted, onUnmounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';

import MonitorHeader from './MonitorHeader.vue';
import AgentColumn from './AgentColumn.vue';
import ConversationPreview from './ConversationPreview.vue';
import CampaignBatchDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignBatch/CampaignBatchDialog.vue';

import ConversationApi from 'dashboard/api/inbox/conversation';

const store = useStore();

const isLoading = ref(true);
const selectedConversation = ref(null);
const statusFilter = ref('open');
const inboxFilter = ref(null);
const onlyWaiting = ref(false);
const pipelineFilter = ref(null);
const stageFilter = ref(null);

const agents = useMapGetter('agents/getVerifiedAgents');

// Per-agent conversation storage + pagination state
const conversationMap = reactive({}); // { agentId|'unassigned': [conversations] }
const pageMap = reactive({}); // { agentId|'unassigned': currentPage }
const hasMoreMap = reactive({}); // { agentId|'unassigned': boolean }
const loadingMap = reactive({}); // { agentId|'unassigned': boolean }
const totalCountMap = reactive({}); // { agentId|'unassigned': totalCount }

const PER_PAGE = 25;

// Sorted agents: by name for grouping
const sortedAgents = computed(() => {
  return [...(agents.value || [])].sort((a, b) => a.name.localeCompare(b.name));
});

// All agents always visible, sorted by conversation count then online status
const visibleAgents = computed(() => {
  return sortedAgents.value
    .sort((a, b) => {
      const aCount = (conversationMap[a.id] || []).length;
      const bCount = (conversationMap[b.id] || []).length;
      if (bCount !== aCount) return bCount - aCount;
      const order = { online: 0, busy: 1, offline: 2 };
      return (
        (order[a.availability_status] ?? 2) -
        (order[b.availability_status] ?? 2)
      );
    });
});

// Sort conversations by most recent activity first
const sortByRecent = conversations => {
  return [...conversations].sort((a, b) => {
    const aTime = new Date(a.last_activity_at || a.updated_at || 0).getTime();
    const bTime = new Date(b.last_activity_at || b.updated_at || 0).getTime();
    return bTime - aTime;
  });
};

// Client-side filters: waiting + pipeline/stage
const applyClientFilters = conversations => {
  let result = conversations;
  if (onlyWaiting.value) {
    result = result.filter(c => {
      const msgs = c.messages || [];
      if (!msgs.length) return false;
      const lastMsg = msgs[msgs.length - 1];
      // Must be incoming (type 0) and NOT a system/activity message
      const isIncoming = lastMsg?.message_type === 0 || lastMsg?.message_type === 'incoming';
      const isActivity = lastMsg?.content_type === 'activity' || lastMsg?.message_type === 2 || lastMsg?.message_type === 'activity';
      return isIncoming && !isActivity;
    });
  }
  if (pipelineFilter.value) {
    result = result.filter(c => {
      const deal = c.crm_deal;
      if (!deal) return false;
      if (deal.pipeline_id !== pipelineFilter.value) return false;
      if (stageFilter.value && deal.stage_id !== stageFilter.value) return false;
      return true;
    });
  }
  return sortByRecent(result);
};

const unassignedConversations = computed(() =>
  applyClientFilters(conversationMap.unassigned || [])
);

const getAgentConversations = agentId =>
  applyClientFilters(conversationMap[agentId] || []);
const isColumnLoading = key => loadingMap[key] || false;
const columnHasMore = key => hasMoreMap[key] ?? false;

// Build filter query for a specific agent
const buildFilterQuery = agentId => {
  const filters = [];
  // Status filter — unassigned always gets open + pending
  if (agentId === 'unassigned') {
    filters.push({
      attribute_key: 'status',
      filter_operator: 'equal_to',
      values: ['open', 'pending'],
      query_operator: 'AND',
    });
  } else if (statusFilter.value === 'all') {
    filters.push({
      attribute_key: 'status',
      filter_operator: 'equal_to',
      values: ['open', 'pending'],
      query_operator: 'AND',
    });
  } else {
    filters.push({
      attribute_key: 'status',
      filter_operator: 'equal_to',
      values: [statusFilter.value],
      query_operator: 'AND',
    });
  }
  // Inbox filter
  if (inboxFilter.value) {
    filters.push({
      attribute_key: 'inbox_id',
      filter_operator: 'equal_to',
      values: [inboxFilter.value],
      query_operator: 'AND',
    });
  }
  // Assignee filter
  if (agentId === 'unassigned') {
    filters.push({
      attribute_key: 'assignee_id',
      filter_operator: 'is_not_present',
      values: [],
      query_operator: null,
    });
  } else {
    filters.push({
      attribute_key: 'assignee_id',
      filter_operator: 'equal_to',
      values: [agentId],
      query_operator: null,
    });
  }
  return filters;
};

// Fetch conversations for a specific agent/unassigned
const fetchForAgent = async (key, page = 1) => {
  if (loadingMap[key]) return;
  loadingMap[key] = true;
  try {
    const { data } = await ConversationApi.filter({
      queryData: { payload: buildFilterQuery(key) },
      page,
    });
    const conversations = data.payload || [];
    const totalCount = data.meta?.all_count || 0;

    if (page === 1) {
      conversationMap[key] = sortByRecent(conversations);
    } else {
      // Append, deduplicate
      const existingIds = new Set((conversationMap[key] || []).map(c => c.id));
      const newConvs = conversations.filter(c => !existingIds.has(c.id));
      conversationMap[key] = sortByRecent([...(conversationMap[key] || []), ...newConvs]);
    }
    totalCountMap[key] = totalCount;
    pageMap[key] = page;
    hasMoreMap[key] =
      (conversationMap[key] || []).length < totalCount &&
      conversations.length >= PER_PAGE;
  } catch (err) {
    // silent
  } finally {
    loadingMap[key] = false;
  }
};

// Load more for a specific column
const loadMore = key => {
  if (!hasMoreMap[key] || loadingMap[key]) return;
  const nextPage = (pageMap[key] || 1) + 1;
  fetchForAgent(key, nextPage);
};

// Initial load: fetch page 1 for all visible agents + unassigned
const fetchAll = async () => {
  isLoading.value = true;
  await store.dispatch('agents/get');
  await store.dispatch('inboxes/get');

  const agentList = agents.value || [];
  // Fetch unassigned + all agents in parallel
  const keys = ['unassigned', ...agentList.map(a => a.id)];
  await Promise.all(keys.map(k => fetchForAgent(k, 1)));
  isLoading.value = false;
};

// Refresh all columns (re-fetch page 1 for everyone)
const refreshAll = async () => {
  const keys = Object.keys(conversationMap);
  // Also include any new agents
  const agentList = agents.value || [];
  const allKeys = new Set([
    ...keys,
    'unassigned',
    ...agentList.map(a => String(a.id)),
  ]);
  await Promise.all([...allKeys].map(k => fetchForAgent(k, 1)));
};

// Handle real-time updates — only refresh if polling is active
const handleConversationUpdate = () => {
  if (pollingEnabled.value) {
    refreshAll();
  }
};

// Handle new message: only if polling is active
const handleNewMessage = data => {
  if (!pollingEnabled.value) return;
  if (!data?.conversation_id) return;
  const convId = data.conversation_id;
  const now = new Date().toISOString();

  // Update last_activity_at in-place and re-sort the column
  for (const key of Object.keys(conversationMap)) {
    const convs = conversationMap[key];
    if (!convs) continue;
    const conv = convs.find(c => c.id === convId);
    if (conv) {
      conv.last_activity_at = now;
      // Update last message preview
      if (data.messages) {
        conv.messages = data.messages;
      }
      conversationMap[key] = sortByRecent(convs);
      break;
    }
  }
};

// --- Polling control ---
const POLLING_DURATION = 5 * 60; // 5 minutes in seconds
const POLLING_INTERVAL = 15000; // 15 seconds

const pollingEnabled = ref(false);
const pollingRemaining = ref(0); // seconds remaining
let refreshInterval = null;
let countdownInterval = null;
let pollingTimeout = null;

const startPolling = () => {
  stopPolling();
  pollingEnabled.value = true;
  pollingRemaining.value = POLLING_DURATION;
  refreshInterval = setInterval(refreshAll, POLLING_INTERVAL);
  countdownInterval = setInterval(() => {
    pollingRemaining.value -= 1;
    if (pollingRemaining.value <= 0) stopPolling();
  }, 1000);
};

const stopPolling = () => {
  pollingEnabled.value = false;
  pollingRemaining.value = 0;
  if (refreshInterval) { clearInterval(refreshInterval); refreshInterval = null; }
  if (countdownInterval) { clearInterval(countdownInterval); countdownInterval = null; }
  if (pollingTimeout) { clearTimeout(pollingTimeout); pollingTimeout = null; }
};

const togglePolling = () => {
  if (pollingEnabled.value) {
    stopPolling();
  } else {
    startPolling();
  }
};

onMounted(async () => {
  await fetchAll();
  emitter.on('fetch_conversation_stats', handleConversationUpdate);
  emitter.on('message.created', handleNewMessage);
  emitter.on('conversation.updated', handleConversationUpdate);
});

onUnmounted(() => {
  emitter.off('fetch_conversation_stats', handleConversationUpdate);
  emitter.off('message.created', handleNewMessage);
  emitter.off('conversation.updated', handleConversationUpdate);
  stopPolling();
});

// Re-fetch when filters change
const onStatusChange = () => {
  isLoading.value = true;
  // Reset all pagination
  Object.keys(pageMap).forEach(k => {
    pageMap[k] = 1;
  });
  refreshAll().then(() => {
    isLoading.value = false;
  });
};

const onInboxChange = () => {
  onStatusChange();
};

const selectConversation = conv => {
  selectedConversation.value = conv;
};

const closePreview = () => {
  selectedConversation.value = null;
};

const onReassigned = () => {
  refreshAll();
  selectedConversation.value = null;
};

// --- Bulk selection ---
const selectMode = ref(false);
const selectedIds = ref([]);
const showBatchDialog = ref(false);

const toggleSelectMode = () => {
  selectMode.value = !selectMode.value;
  if (!selectMode.value) selectedIds.value = [];
};

const toggleSelect = conv => {
  const idx = selectedIds.value.indexOf(conv.id);
  if (idx >= 0) {
    selectedIds.value.splice(idx, 1);
  } else {
    selectedIds.value.push(conv.id);
  }
};

const toggleSelectAllColumn = conversations => {
  const allSelected = conversations.every(c => selectedIds.value.includes(c.id));
  if (allSelected) {
    selectedIds.value = selectedIds.value.filter(id => !conversations.some(c => c.id === id));
  } else {
    const newIds = conversations.filter(c => !selectedIds.value.includes(c.id)).map(c => c.id);
    selectedIds.value.push(...newIds);
  }
};

const cancelSelection = () => {
  selectedIds.value = [];
  selectMode.value = false;
};

// Get contact_ids from selected conversations for batch creation
const selectedContactIds = computed(() => {
  const ids = [];
  for (const key of Object.keys(conversationMap)) {
    const convs = conversationMap[key] || [];
    for (const c of convs) {
      if (selectedIds.value.includes(c.id) && c.meta?.sender?.id) {
        ids.push(c.meta.sender.id);
      }
    }
  }
  return [...new Set(ids)];
});

const openBatchDialog = () => {
  showBatchDialog.value = true;
};

const onBatchCreated = () => {
  showBatchDialog.value = false;
  cancelSelection();
};

// --- Bulk reassign ---
const showReassignDropdown = ref(false);
const isBulkProcessing = ref(false);

const bulkReassign = async (agentId) => {
  showReassignDropdown.value = false;
  isBulkProcessing.value = true;
  try {
    const ids = [...selectedIds.value];
    await Promise.allSettled(
      ids.map(convId =>
        ConversationApi.assignAgent({ conversationId: convId, agentId })
      )
    );
    cancelSelection();
    await refreshAll();
  } catch {
    // silent
  } finally {
    isBulkProcessing.value = false;
  }
};

// --- Bulk status change ---
const showStatusDropdown = ref(false);

const bulkChangeStatus = async (newStatus) => {
  showStatusDropdown.value = false;
  isBulkProcessing.value = true;
  try {
    const ids = [...selectedIds.value];
    await Promise.allSettled(
      ids.map(convId =>
        ConversationApi.toggleStatus({ conversationId: convId, status: newStatus })
      )
    );
    cancelSelection();
    await refreshAll();
  } catch {
    // silent
  } finally {
    isBulkProcessing.value = false;
  }
};

const statusOptions = [
  { value: 'open', label: 'Aberta', icon: 'i-lucide-circle-dot', color: 'text-green-500' },
  { value: 'pending', label: 'Pendente', icon: 'i-lucide-clock', color: 'text-yellow-500' },
  { value: 'resolved', label: 'Resolvida', icon: 'i-lucide-check-circle', color: 'text-blue-500' },
];
</script>

<template>
  <div class="flex flex-col h-full w-full">
    <MonitorHeader
      v-model:status="statusFilter"
      v-model:inbox-id="inboxFilter"
      v-model:only-waiting="onlyWaiting"
      v-model:pipeline-id="pipelineFilter"
      v-model:stage-id="stageFilter"
      :select-mode="selectMode"
      :polling-enabled="pollingEnabled"
      :polling-remaining="pollingRemaining"
      @update:status="onStatusChange"
      @update:inbox-id="onInboxChange"
      @toggle-select-mode="toggleSelectMode"
      @toggle-polling="togglePolling"
    />

    <div v-if="isLoading" class="flex-grow flex items-center justify-center">
      <div class="flex flex-col items-center gap-3">
        <span class="i-lucide-loader-2 size-8 text-n-brand animate-spin" />
        <p class="text-sm text-n-slate-10">Carregando atendimentos...</p>
      </div>
    </div>

    <div v-else class="flex-grow overflow-x-auto overflow-y-hidden px-4 py-4">
      <div class="flex gap-3 h-full min-w-max">
        <AgentColumn
          is-unassigned
          :conversations="unassignedConversations"
          :is-loading-more="isColumnLoading('unassigned')"
          :has-more="columnHasMore('unassigned')"
          :total-count="totalCountMap['unassigned'] ?? 0"
          :select-mode="selectMode"
          :selected-ids="selectedIds"
          @select-conversation="selectConversation"
          @load-more="loadMore('unassigned')"
          @toggle-select="toggleSelect"
          @select-all-column="toggleSelectAllColumn"
        />

        <AgentColumn
          v-for="agent in visibleAgents"
          :key="agent.id"
          :agent="agent"
          :conversations="getAgentConversations(agent.id)"
          :is-loading-more="isColumnLoading(agent.id)"
          :has-more="columnHasMore(agent.id)"
          :total-count="totalCountMap[agent.id] ?? 0"
          :select-mode="selectMode"
          :selected-ids="selectedIds"
          @select-conversation="selectConversation"
          @load-more="loadMore(agent.id)"
          @toggle-select="toggleSelect"
          @select-all-column="toggleSelectAllColumn"
        />
      </div>
    </div>

    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      leave-active-class="transition-all duration-150 ease-in"
      enter-from-class="translate-y-full opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-full opacity-0"
    >
      <div
        v-if="selectMode && selectedIds.length > 0"
        class="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-5 py-3 rounded-xl bg-n-slate-12 dark:bg-n-solid-3 text-white dark:text-n-slate-12 shadow-2xl border border-white/10"
      >
        <div class="flex items-center gap-2">
          <span class="inline-flex items-center justify-center size-6 rounded-full bg-blue-500 text-white text-xs font-bold">
            {{ selectedIds.length }}
          </span>
          <span class="text-sm font-medium">selecionados</span>
        </div>
        <div class="w-px h-5 bg-white/20" />

        <span v-if="isBulkProcessing" class="i-lucide-loader-2 size-4 animate-spin text-blue-400" />

        <button
          :disabled="isBulkProcessing"
          class="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors disabled:opacity-50"
          @click="openBatchDialog"
        >
          <span class="i-lucide-send size-3.5" />
          Lote de Envio
        </button>

        <div class="relative">
          <button
            :disabled="isBulkProcessing"
            class="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50"
            @click.stop="showReassignDropdown = !showReassignDropdown; showStatusDropdown = false"
          >
            <span class="i-lucide-user-check size-3.5" />
            Reatribuir
            <span class="i-lucide-chevron-up size-3" />
          </button>
          <Transition
            enter-active-class="transition-all duration-150 ease-out"
            leave-active-class="transition-all duration-100 ease-in"
            enter-from-class="opacity-0 translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-2"
          >
            <div
              v-if="showReassignDropdown"
              class="absolute bottom-full mb-2 left-0 w-56 max-h-64 overflow-y-auto rounded-xl bg-n-background dark:bg-n-solid-3 border border-n-weak shadow-2xl py-1"
              @click.stop
            >
              <button
                class="flex items-center gap-2.5 w-full px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                @click="bulkReassign(null)"
              >
                <span class="i-lucide-user-x size-4 text-n-slate-9" />
                <span>Sem atribuição</span>
              </button>
              <div class="h-px bg-n-weak mx-2 my-1" />
              <button
                v-for="agent in sortedAgents"
                :key="agent.id"
                class="flex items-center gap-2.5 w-full px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                @click="bulkReassign(agent.id)"
              >
                <img
                  v-if="agent.thumbnail"
                  :src="agent.thumbnail"
                  :alt="agent.name"
                  class="size-5 rounded-full object-cover"
                />
                <span
                  v-else
                  class="size-5 rounded-full bg-n-brand/10 text-n-brand flex items-center justify-center text-[10px] font-semibold flex-shrink-0"
                >
                  {{ (agent.name || '?')[0].toUpperCase() }}
                </span>
                <span class="truncate">{{ agent.name }}</span>
                <span
                  class="size-2 rounded-full ml-auto flex-shrink-0"
                  :class="{
                    'bg-green-500': agent.availability_status === 'online',
                    'bg-yellow-500': agent.availability_status === 'busy',
                    'bg-n-slate-7': agent.availability_status === 'offline',
                  }"
                />
              </button>
            </div>
          </Transition>
        </div>

        <div class="relative">
          <button
            :disabled="isBulkProcessing"
            class="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50"
            @click.stop="showStatusDropdown = !showStatusDropdown; showReassignDropdown = false"
          >
            <span class="i-lucide-repeat size-3.5" />
            Alterar Status
            <span class="i-lucide-chevron-up size-3" />
          </button>
          <Transition
            enter-active-class="transition-all duration-150 ease-out"
            leave-active-class="transition-all duration-100 ease-in"
            enter-from-class="opacity-0 translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-2"
          >
            <div
              v-if="showStatusDropdown"
              class="absolute bottom-full mb-2 left-0 w-48 rounded-xl bg-n-background dark:bg-n-solid-3 border border-n-weak shadow-2xl py-1"
              @click.stop
            >
              <button
                v-for="opt in statusOptions"
                :key="opt.value"
                class="flex items-center gap-2.5 w-full px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                @click="bulkChangeStatus(opt.value)"
              >
                <span :class="[opt.icon, 'size-4', opt.color]" />
                <span>{{ opt.label }}</span>
              </button>
            </div>
          </Transition>
        </div>

        <div class="w-px h-5 bg-white/20" />
        <button
          class="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
          @click="cancelSelection"
        >
          <span class="i-lucide-x size-3.5" />
          Cancelar
        </button>
      </div>
    </Transition>

    <ConversationPreview
      v-if="selectedConversation"
      :conversation="selectedConversation"
      @close="closePreview"
      @reassigned="onReassigned"
    />

    <CampaignBatchDialog
      v-if="showBatchDialog"
      :preselected-contact-ids="selectedContactIds"
      @close="showBatchDialog = false"
      @success="onBatchCreated"
    />
  </div>
</template>
